import { AccountRole } from '@/data/usecases/account/account-role'
import { AccountType } from '@/data/usecases/account/account-type'
import { ContractedPlanLicense } from '@/data/usecases/plan-license/contracted-plan-license'
import { DocumentModel } from '@/domain/models/document/document-model'
import { DocumentType } from '@/domain/models/document/document-type'
import { Account } from '@/domain/usecases'
import { AccountBenefit } from '@/domain/usecases/account/account-benefit'
import { AccountProfessional } from '@/domain/usecases/account/account-professional'
import { CompanyRefundProgram } from '@/domain/usecases/company/company-refund-program'
import { OnboardingOptions } from '@/domain/usecases/onboarding/onboarding-options'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import CompanyManagerBaseLoader from '@/main/routes/loaders/company-manager-base-loader'
import PathConstants from '@/main/routes/path-contants'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'
import {
  UserProfileLoaderBenefitOptionsType,
  UserProfileLoaderDataType,
  UserProfileLoaderProfessionalOptionsType
} from '../../user/user-profile-loader'

export type CompanyManagerCreateUserProfileLoaderDataType = UserProfileLoaderDataType

export default class CompanyManagerCreateUserProfileLoader extends CompanyManagerBaseLoader {
  constructor(
    readonly account: Account.Model,
    readonly queryClient: QueryClient,
    readonly ldClient: LDClient | undefined,
    readonly redirectUrl: string = PathConstants.ExternalRoutes.MONOLITH_MANAGE_CUSTOMER.value
  ) {
    super(account, redirectUrl, queryClient, ldClient)
  }

  private getAccountProfileData(): Account.ProfileModel {
    return {
      id: '',
      accountType: AccountType.Enum.COLLABORATOR,
      document: new DocumentModel(DocumentType.Enum.CPF, ''),
      name: '',
      email: '',
      onboardingCompleted: false,
      onboardingCompletedAt: null,
      roles: [],
      company: this.account.company,
      addons: this.account.addons,
      preferenceLanguage: ''
    }
  }

  private async getProfessionalOptions(): Promise<
    UserProfileLoaderProfessionalOptionsType | undefined
  > {
    if (AccountType.Enum.DEPENDENT.is(this.account)) return undefined
    return super
      .importRemoteModule('makeRemoteOnboardingOptions')
      .then(async makeRemoteOnboardingOptions => {
        const remoteOnboardingOptions = makeRemoteOnboardingOptions(
          this.queryClient
        ) as OnboardingOptions
        return remoteOnboardingOptions.getStep1()
      })
      .then(data => {
        return {
          areas: data.professionalAreas,
          seniorities: data.professionalSeniorities
        }
      })
  }

  private getAccountProfessional(): AccountProfessional.Model {
    const { company } = this.account
    return {
      company: company,
      requiresApproval: company.requiresApproval
    }
  }

  private async getCompanyRefundPrograms(
    accountProfile: Account.ProfileModel
  ): Promise<CompanyRefundProgram.ListModel> {
    if (!accountProfile.company.requiresApproval) return []
    return import('@remote/company/remote-company-refund-program-factory')
      .then(module => module.makeRemoteCompanyRefundProgram)
      .then(async makeRemote => {
        const remote = makeRemote(accountProfile.company.uniqueId, this.queryClient)
        return remote.loader()
      })
  }

  private async getBenefitsOptions(
    accountProfile: Account.ProfileModel
  ): Promise<AccountBenefit.OptionsModel> {
    return Promise.all([
      import('@remote/account/company-manager/remote-company-manager-plan-license-factory')
        .then(module => module.makeRemoteCompanyPlanLicense)
        .then(async makeRemotePlanLicense => {
          const remote = makeRemotePlanLicense(accountProfile.company.uniqueId, this.queryClient)
          return remote.loader()
        }),
      this.getCompanyRefundPrograms(accountProfile)
    ]).then(([licenses, refundPrograms]) => {
      return {
        passPlans: licenses
          .filter(license => accountProfile.accountType === license.accountType)
          .map(license => ({
            id: license.id,
            name: license.name,
            contracted: !!license.available
          })) as ContractedPlanLicense.ListModel,
        refundPrograms
      }
    })
  }

  private async getAccountBenefits(
    accountProfile: Account.ProfileModel
  ): Promise<UserProfileLoaderBenefitOptionsType> {
    const options = await this.getBenefitsOptions(accountProfile)
    const { refundPrograms } = options
    return {
      benefitsOptions: options,
      accountBenefits: {
        contractedPlan: {
          plan: null
        },
        addons: {
          refund: {
            enabled: !!refundPrograms?.length
          } as Account.AddonModel
        }
      }
    }
  }

  private verifyPermission(): boolean {
    return FeatureFlag.Key.RH_CREATE_COLLABORATOR_PROFILE.getValue(this.ldClient)
  }

  async loader() {
    if (AccountRole.Enum.COMPANY_MANAGER.isNot(this.account)) {
      return super.notAllowedLoader()
    }

    const accountProfile = this.getAccountProfileData()
    if (!this.verifyPermission()) {
      return super.notAllowedLoader()
    }

    return this.deferExec({
      data: Promise.all([
        this.getAccountProfessional(),
        this.getAccountBenefits(accountProfile),
        this.getProfessionalOptions()
      ]).then(
        ([accountProfessional, { accountBenefits, benefitsOptions }, professionalOptions]) => ({
          accountProfile,
          accountProfessional,
          accountBenefits,
          benefitsOptions,
          professionalOptions
        })
      )
    })
  }
}
